// DownloadScorecardButton — "Download Summary Scorecard" control in the
// header's far-right slot (US-882 · R-1/R-4/R-7/R-8).
//
// One button, four states over useReportJob: idle (enabled once a country is
// selected), processing (spinner, disabled), ready (becomes the DOCX download
// link), error/timeout (retry, with the message in the tooltip). The job
// resets when the selected country changes — a stale report must never be
// downloadable under another country's name (R-8).
//
// Session gate (R-7): the header only renders inside the authed tree (App.js
// swaps Login ↔ Dashboard), so an unauthenticated user never sees this
// control; if the server session expires mid-use, the trigger's 401 surfaces
// through the error state (ApiError detail) with retry.
import { useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import useUI from "../../hooks/useUI";
import useReportJob from "../../hooks/useReportJob";
import { countryRequestBody } from "../../config/simulationDefaults";

// Styled for the navy header, mirroring CountryDropdown's translucent field.
const HEADER_BUTTON_SX = {
  color: "primary.contrastText",
  borderColor: "rgba(255,255,255,0.28)",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:hover": {
    borderColor: "rgba(255,255,255,0.5)",
    bgcolor: "rgba(255,255,255,0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.45)",
    borderColor: "rgba(255,255,255,0.16)",
  },
};

// Maps the job state to what the one button shows and does.
const presentation = ({ status, wordReportUrl, error, iso3, trigger, retry }) => {
  if (status === "processing") {
    return {
      label: "Preparing report…",
      tooltip: "Generating the Summary Scorecard…",
      icon: <CircularProgress size={16} color="inherit" />,
      buttonProps: { disabled: true },
    };
  }
  if (status === "ready") {
    return {
      label: "Save report (DOCX)",
      tooltip: "The Summary Scorecard is ready — save the Word file.",
      icon: <FileDownloadOutlinedIcon />,
      // target/_blank keeps the SPA alive even if the file host does not send
      // Content-Disposition: attachment.
      buttonProps: {
        component: "a",
        href: wordReportUrl,
        target: "_blank",
        rel: "noopener",
      },
    };
  }
  if (status === "error") {
    return {
      label: "Retry report",
      tooltip: error || "The report failed — try again.",
      icon: <ReplayRoundedIcon />,
      buttonProps: { onClick: retry },
    };
  }
  return {
    label: "Download Scorecard",
    tooltip: iso3
      ? "Generate the country's Summary Scorecard (Word)."
      : "Select a country to enable the report.",
    icon: <FileDownloadOutlinedIcon />,
    buttonProps: iso3
      ? { onClick: () => trigger(countryRequestBody(iso3)) }
      : { disabled: true },
  };
};

const DownloadScorecardButton = () => {
  const { selectedCountry } = useUI();
  const iso3 = selectedCountry?.iso3 || null;
  const { status, wordReportUrl, error, trigger, retry, reset } = useReportJob();

  // A new country invalidates the previous job and its result (R-8).
  useEffect(() => {
    reset();
  }, [iso3, reset]);

  const { label, tooltip, icon, buttonProps } = presentation({
    status,
    wordReportUrl,
    error,
    iso3,
    trigger,
    retry,
  });

  return (
    <Tooltip title={tooltip}>
      {/* span keeps the tooltip alive over a disabled button */}
      <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={icon}
          sx={HEADER_BUTTON_SX}
          {...buttonProps}
        >
          {label}
        </Button>
      </Box>
    </Tooltip>
  );
};

export default DownloadScorecardButton;
